import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, CreditCard, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import PointPackagesTab from '@/components/products/PointPackagesTab';
import SubscriptionsTab from '@/components/products/SubscriptionsTab';
import ShopProductsTab from '@/components/products/ShopProductsTab';
import ShopSettingsTab from '@/components/products/ShopSettingsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('points');
  const [loading, setLoading] = useState(true);

  // Data states
  const [pointPackages, setPointPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [services, setServices] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [recurringDiscount, setRecurringDiscount] = useState(5);
  const [creditPackageDiscount, setCreditPackageDiscount] = useState(0);

  // Load flags
  const [baseLoaded, setBaseLoaded] = useState(false);
  const [shopLoaded, setShopLoaded] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);

  const loadTabData = async (tab) => {
    if ((tab === 'points' || tab === 'subscriptions') && !baseLoaded) {
      await fetchBaseData();
    }
    if (tab === 'shop' && !shopLoaded) {
      await fetchShopProducts();
    }
    if (tab === 'settings' && !settingsLoaded) {
      await fetchShopSettings();
    }
  };

  const fetchBaseData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [packagesRes, subsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/point-packages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPointPackages(packagesRes.data);
      setSubscriptions(subsRes.data.filter(s => !s.customer_id));
      setServices(servicesRes.data);
      setBaseLoaded(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchShopProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/products?include_inactive=true`, { headers: { Authorization: `Bearer ${token}` } });
      setShopProducts(res.data);
      setShopLoaded(true);
    } catch (error) {
      console.error('Error fetching shop products:', error);
    }
  };

  const fetchShopSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/settings/shop`, { headers: { Authorization: `Bearer ${token}` } });
      setRecurringDiscount(res.data.recurring_subscription_discount || 5);
      setCreditPackageDiscount(res.data.credit_package_discount || 0);
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error fetching shop settings:', error);
    }
  };

  if (loading && !baseLoaded) {
    return <div className="p-6">Lädt...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          {!embedded && (
            <Button variant="outline" className="mb-3" onClick={() => navigate('/settings')} data-testid="products-back-to-settings-button">
              ← Zurück zu Einstellungen
            </Button>
          )}
          <h1 className="text-3xl font-bold text-gray-900">Produkte</h1>
          <p className="text-gray-600 mt-1">Verwalten Sie Punkt-Pakete und Abos zentral</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="products-tabs">
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="points" data-testid="products-tab-points">
            <Package className="w-4 h-4 mr-2" />Punkt-Pakete
          </TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="products-tab-subscriptions">
            <CreditCard className="w-4 h-4 mr-2" />Abos
          </TabsTrigger>
          <TabsTrigger value="shop" data-testid="shop-products-tab">
            Produkte
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="products-tab-settings">
            <Settings className="w-4 h-4 mr-2" />Einstellungen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="points">
          <PointPackagesTab packages={pointPackages} services={services} onRefresh={fetchBaseData} />
        </TabsContent>

        <TabsContent value="subscriptions">
          <SubscriptionsTab subscriptions={subscriptions} services={services} recurringDiscount={recurringDiscount} onRefresh={fetchBaseData} />
        </TabsContent>

        <TabsContent value="shop">
          <ShopProductsTab products={shopProducts} onRefresh={fetchShopProducts} />
        </TabsContent>

        <TabsContent value="settings">
          <ShopSettingsTab
            recurringDiscount={recurringDiscount}
            setRecurringDiscount={setRecurringDiscount}
            creditPackageDiscount={creditPackageDiscount}
            setCreditPackageDiscount={setCreditPackageDiscount}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductsPage;
